import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import { generateRecurringDates } from './generateRecurringDates';
import moment from 'moment';
import './AddAvailability.css'; // 💅 Import du CSS personnalisé
import { getAuthServerBase } from '../utils/runtime';

const AUTH_SERVER = getAuthServerBase();

Modal.setAppElement('#root');

function AddAvailability() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [services, setServices] = useState([]);
  const [specialities, setSpecialities] = useState([]);
  const [form, setForm] = useState({
    service_id: '',
    speciality_id: '',
    mode: 'in_person',
    recurrence: false,
    frequency: 'hebdomadaire',
    selectedDays: [],
    endDate: '',
    occurrences: '',
    selectedDate: '',
    start_time: '',
    end_time: ''
  });
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetch(`${AUTH_SERVER}/me`, { credentials: 'include' })
      .then(res => res.json())
      .then(setUser)
      .catch(() => {
        alert('Non connecté');
        navigate('/');
      });
  }, [navigate]);

  useEffect(() => {
    if (!user?.email) return;

    const fetchData = async () => {
      const res = await fetch(`https://1irywxa5c3.execute-api.ca-central-1.amazonaws.com/prod/users/email/${user.email}`);
      const data = await res.json();
      const id = data.user_id;

      const specRes = await fetch(`https://1irywxa5c3.execute-api.ca-central-1.amazonaws.com/prod/professionals/${id}/specialities`);
      const specData = await specRes.json();
      setSpecialities(specData);

      const servRes = await fetch(`https://1irywxa5c3.execute-api.ca-central-1.amazonaws.com/prod/professionals/${id}/services`);
      const servData = await servRes.json();
      setServices(servData);
    };

    fetchData();
  }, [user]);

  const handleChange = e => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const toggleDay = (day) => {
    const parsed = parseInt(day);
    if (parsed < 1 || parsed > 5) return;
    setForm(prev => {
      const days = prev.selectedDays.includes(day)
        ? prev.selectedDays.filter(d => d !== day)
        : [...prev.selectedDays, day];
      return { ...prev, selectedDays: days };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.email) return;

    if (!form.start_time || !form.end_time || !form.service_id || !form.selectedDate) {
      alert("Veuillez remplir tous les champs obligatoires.");
      return;
    }

    const res = await fetch(`https://1irywxa5c3.execute-api.ca-central-1.amazonaws.com/prod/users/email/${user.email}`);
    const data = await res.json();
    const professionalId = data.user_id;

    const allDates = generateRecurringDates(form);
    if (allDates.length === 0) {
      alert("Aucune date générée.");
      return;
    }

    const grouped = new Map();
    for (const dateStr of allDates) {
      const postgresDay = moment(dateStr).isoWeekday();
      if (!grouped.has(postgresDay)) grouped.set(postgresDay, []);
      grouped.get(postgresDay).push(dateStr);
    }

    for (const [day, dates] of grouped.entries()) {
      const sortedDates = dates.sort();
      const payload = {
        service_id: form.service_id,
        day_of_week: day,
        start_time: form.start_time,
        end_time: form.end_time,
        mode: form.mode,
        valid_from: sortedDates[0],
        valid_to: sortedDates[sortedDates.length - 1]
      };

      const resp = await fetch(
        `https://1irywxa5c3.execute-api.ca-central-1.amazonaws.com/prod/professionals/${professionalId}/weekly-availabilities`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      if (!resp.ok) {
        const errorText = await resp.text();
        alert(`❌ Erreur lors de l'ajout du créneau pour le jour ${day} : ${errorText}`);
        return;
      }
    }

    alert('✅ Tous les créneaux ont été ajoutés avec succès.');
    navigate('/professional/calendrier');
  };

  return (
    <div className="availability-container">
      <h2 className="availability-title">Ajouter une disponibilité</h2>
      <form onSubmit={handleSubmit} className="availability-form">
        <label>Spécialité :</label>
        <select name="speciality_id" value={form.speciality_id} onChange={handleChange} required>
          <option value="">-- Choisir une spécialité --</option>
          {specialities.map(s => (
            <option key={s.speciality_id} value={s.speciality_id}>{s.name}</option>
          ))}
        </select>

        <label>Service :</label>
        <select name="service_id" value={form.service_id} onChange={handleChange} required>
          <option value="">-- Choisir un service --</option>
          {services.map(s => (
            <option key={s.service_id} value={s.service_id}>{s.name}</option>
          ))}
        </select>

        <label>Date de disponibilité :</label>
        <button type="button" className="btn-secondary" onClick={() => setModalOpen(true)}>Choisir les détails</button>

        <button type="submit" className="btn-primary">Enregistrer</button>
      </form>

      <Modal isOpen={modalOpen} onRequestClose={() => setModalOpen(false)} className="modal-content" overlayClassName="modal-overlay">
        <h2>Détails de la disponibilité</h2>
        <div className="modal-field">
          <label>Date :</label>
          <input type="date" name="selectedDate" value={form.selectedDate} onChange={handleChange} />
        </div>
        <div className="modal-field">
          <label>Heure de début :</label>
          <input type="time" name="start_time" value={form.start_time} onChange={handleChange} />
        </div>
        <div className="modal-field">
          <label>Heure de fin :</label>
          <input type="time" name="end_time" value={form.end_time} onChange={handleChange} />
        </div>
        <div className="modal-field">
          <label>Mode :</label>
          <select name="mode" value={form.mode} onChange={handleChange}>
            <option value="in_person">En personne</option>
            <option value="virtual">Virtuel</option>
          </select>
        </div>
        <div className="modal-field">
          <label>
            <input type="checkbox" checked={form.recurrence} onChange={() => setForm(prev => ({ ...prev, recurrence: !prev.recurrence }))} /> Récurrence
          </label>
        </div>

        {form.recurrence && (
          <>
            <div className="modal-field">
              <label>Fréquence :</label>
              <select name="frequency" value={form.frequency} onChange={handleChange}>
                <option value="journaliere">Journalière</option>
                <option value="hebdomadaire">Hebdomadaire</option>
                <option value="mensuelle">Mensuelle</option>
              </select>
            </div>
            {form.frequency === 'hebdomadaire' && (
              <div className="modal-field">
                <label>Jours de la semaine :</label>
                <div className="weekday-checkboxes">
                  {["Lun", "Mar", "Mer", "Jeu", "Ven"].map((day, i) => (
                    <label key={i}>
                      <input
                        type="checkbox"
                        checked={form.selectedDays.includes((i + 1).toString())}
                        onChange={() => toggleDay((i + 1).toString())}
                      /> {day}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="modal-field">
              <label>Date de fin :</label>
              <input type="date" name="endDate" value={form.endDate} onChange={handleChange} />
            </div>
            <div className="modal-field">
              <label>Ou nombre d'occurrences :</label>
              <input type="number" name="occurrences" value={form.occurrences} onChange={handleChange} />
            </div>
          </>
        )}

        <div className="modal-actions">
          <button onClick={() => setModalOpen(false)} className="btn-primary">OK</button>
        </div>
      </Modal>
    </div>
  );
}

export default AddAvailability;
